import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ArrowDown from '../../assets/arrow_down.svg';
import ArrowRightGrey from '../../assets/arrow_right_grey.svg';
import FooterSectionImage1 from '../../assets/footer_section_image1.png';
import WebAwardLogo from '../../assets/web_award_logo.svg';
import { HorizontalLayout, useResponsivePadding, VerticalLayout } from '../../common/common-layout';
import { TitleNormal, TitleNormalBold } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import { fetchCopyright } from '../../common/firebase/FirestoreService';
import i18n from '../../common/i18n/i18n';
import { pxToRem } from '../../common/utils/ConvertUtils';
import { MULTIPLE_LANGUAGE } from '../../config';
import '../../css/bbpage.css';
import Copyright from '../../model/Copyright';

const FooterSection = () => {
  const { t } = useTranslation();
  const { horizontal: padding } = useResponsivePadding(/*{ mode: 'fixed' }*/);
  const navigate = useNavigate();
  const [copyright, setCopyright] = useState<Copyright | null>(null);
  const isEng = ['en', 'en-mobile'].includes(i18n.language);
  const isChina = ['zh', 'zh-mobile'].includes(i18n.language);

  function getCurrentLangName(lang: string): string {
    switch (lang) {
      case 'ko':
        return '한국어';
      case 'en':
        return 'English';
      case 'zh':
        return '简体中文';
      default:
        return '한국어';
    }
  }
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const TITLE_BOTTOM_MARGIN = 30;
  const SUB_MENU_GAP = 20;

  const currentLangName = getCurrentLangName(i18n.language);
  const languages = [
    { code: 'ko', name: '한국어' },
    { code: 'en', name: 'English' },
    { code: 'zh', name: '简体中文' },
  ];

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const copyRight = await fetchCopyright();
      setCopyright(copyRight);
    };

    fetchData();
  }, []);

  return (
    <VerticalLayout
      style={{
        background: 'black',
        paddingLeft: padding,
        paddingRight: padding,
        paddingBottom: `${pxToRem(54)}`
      }}
    >
      <HorizontalLayout>
        <VerticalLayout wrapContents={true} style={{ width: isEng ? '52%' : '45%' }}>
          <BTBox height={`${pxToRem(67)}`} />
          <img src={FooterSectionImage1} style={{ width: `${pxToRem(78)}`, height: `${pxToRem(100)}`, cursor: 'pointer' }} onClick={() => {
            //console.log('로고 클릭됨');
            navigate('/');
          }} />
          <BTBox height={42} />
          <TitleNormalBold fontSize={pxToRem(18)} color='white'>{t(`${copyright?.companyName}`)}</TitleNormalBold>
          <BTBox height={22} />
          <TitleNormal fontSize={pxToRem(14)} color='white'>{t(`${copyright?.address}`)}</TitleNormal>
          <BTBox height={pxToRem(11)} />
          <HorizontalLayout wrapContents={true}>
            <TitleNormal fontSize={pxToRem(14)} color='white'>
              {t('사업자등록번호')}
              &nbsp;
              {t(copyright?.corpRegNo ?? '')}
            </TitleNormal>
            <BTBox height={10} />
            <TitleNormal fontSize={pxToRem(14)} color='white' style={{ opacity: 0.2 }}>|</TitleNormal>
            <BTBox height={10} />
            <TitleNormal fontSize={pxToRem(14)} color='white'>
              {t('대표자.')}
              &nbsp;
              {t(copyright?.representPersonName ?? '')}
            </TitleNormal>
          </HorizontalLayout>
          <BTBox height={pxToRem(17)} />
          <TitleNormal fontSize={pxToRem(14)} color='white' style={{ opacity: '0.5' }}>{copyright?.copyright}</TitleNormal>
        </VerticalLayout>
        <VerticalLayout wrapContents={true}>
          <BTBox height={`${pxToRem(65)}`} />
          <HorizontalLayout>
            {/** 메뉴 항목들은 유지된 한글을 t()로 감쌌습니다. */}
            <VerticalLayout>
              <TitleNormalBold fontSize={pxToRem(16)} color='white'>{t('회사소개').toUpperCase()}</TitleNormalBold>
              <BTBox height={pxToRem(TITLE_BOTTOM_MARGIN)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/introduce/vision')}>{t('경영비전')}</TitleNormal>
              <BTBox height={pxToRem(SUB_MENU_GAP)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/introduce/ceo')}>{t('CEO 메시지')}</TitleNormal>
              <BTBox height={pxToRem(SUB_MENU_GAP)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/introduce/history')}>{t('회사연혁')}</TitleNormal>
              <BTBox height={pxToRem(SUB_MENU_GAP)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/introduce/qualification')}>{t('특허 및 인증')}</TitleNormal>
              <BTBox height={pxToRem(SUB_MENU_GAP)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/introduce/organization')}>{t('조직도')}</TitleNormal>
              <BTBox height={pxToRem(SUB_MENU_GAP)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/introduce/ci')}>{t('CI')}</TitleNormal>
              {(!isEng && !isChina) && (
                <>
                  <BTBox height={pxToRem(SUB_MENU_GAP)} />
                  <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/introduce/location')}>{t('찾아오는 길')}</TitleNormal>
                </>
              )}
            </VerticalLayout>

            <VerticalLayout>
              <TitleNormalBold fontSize={pxToRem(16)} color='white'>{t('사업영역').toUpperCase()}</TitleNormalBold>
              <BTBox height={pxToRem(TITLE_BOTTOM_MARGIN)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/business')}>{t('반도체')}</TitleNormal>
              <BTBox height={pxToRem(SUB_MENU_GAP)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/business/battery')}>{t('이차전지')}</TitleNormal>
              <BTBox height={pxToRem(SUB_MENU_GAP)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/business/datacenter')}>{t('데이터센터')}</TitleNormal>
              <BTBox height={pxToRem(SUB_MENU_GAP)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/business/dryroom')}>{t('클린룸/드라이룸')}</TitleNormal>
              <BTBox height={pxToRem(SUB_MENU_GAP)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/business/teflon')}>{t('테프론코팅')}</TitleNormal>
              <BTBox height={pxToRem(SUB_MENU_GAP)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/business/cryogenic')}>{t('극저온배관')}</TitleNormal>
            </VerticalLayout>

            {(!isEng && !isChina) && (
              <VerticalLayout>
                <TitleNormalBold fontSize={pxToRem(16)} color='white'>{t('홍보채널').toUpperCase()}</TitleNormalBold>
                <BTBox height={pxToRem(TITLE_BOTTOM_MARGIN)} />
                <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/promotion')}>{t('미디어센터')}</TitleNormal>
                <BTBox height={pxToRem(SUB_MENU_GAP)} />
                <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/promotion/news')}>{t('사내소식')}</TitleNormal>
              </VerticalLayout>
            )}

            {(!isEng && !isChina) && (
              <VerticalLayout>
                <TitleNormalBold fontSize={pxToRem(16)} color='white'>{t('고객지원').toUpperCase()}</TitleNormalBold>
                <BTBox height={pxToRem(TITLE_BOTTOM_MARGIN)} />
                <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/customer/inquiry')}>{t('문의하기')}</TitleNormal>
                <BTBox height={pxToRem(SUB_MENU_GAP)} />
                <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/customer/report')}>{t('BB열린소리')}</TitleNormal>
              </VerticalLayout>
            )}

            {(!isEng && !isChina) ? (
              <VerticalLayout>
                <TitleNormalBold fontSize={pxToRem(16)} color='white'>{t('채용').toUpperCase()}</TitleNormalBold>
                <BTBox height={pxToRem(TITLE_BOTTOM_MARGIN)} />
                <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/recruit')}>{t('채용공고')}</TitleNormal>
                <BTBox height={pxToRem(SUB_MENU_GAP)} />
                <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/recruit/people')}>{t('인재상')}</TitleNormal>
                <BTBox height={pxToRem(SUB_MENU_GAP)} />
                <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/recruit/benefits')}>{t('복지제도')}</TitleNormal>
                <BTBox height={pxToRem(SUB_MENU_GAP)} />
                <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/recruit/bbpeople')}>{t('직무소개')}</TitleNormal>
              </VerticalLayout>
            ) : (
              <VerticalLayout>
                <TitleNormalBold fontSize={pxToRem(16)} color='white'>{t('채용').toUpperCase()}</TitleNormalBold>
                <BTBox height={pxToRem(TITLE_BOTTOM_MARGIN)} />
                <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/recruit/people')}>{t('인재상')}</TitleNormal>
              </VerticalLayout>
            )}
            <VerticalLayout>
              <TitleNormalBold fontSize={pxToRem(16)} color='white' onClick={() => navigate('/terms/policy')}>{t('개인정보처리방침').toUpperCase()}</TitleNormalBold>
              <BTBox height={pxToRem(TITLE_BOTTOM_MARGIN)} />
              <TitleNormal fontSize={pxToRem(16)} color='white' onClick={() => navigate('/terms')}>{t('이용약관')}</TitleNormal>
              <BTBox height={pxToRem(SUB_MENU_GAP)} />
              <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
                {MULTIPLE_LANGUAGE && (
                  <HorizontalLayout style={{ alignItems: 'center', cursor: 'pointer' }} onClick={() => setIsOpen(prev => !prev)}>
                    <TitleNormal fontSize={pxToRem(16)} color='white'>{currentLangName}</TitleNormal>
                    <BTBox width={5} />
                    <img src={ArrowDown} />
                  </HorizontalLayout>
                )}


                {isOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      backgroundColor: '#fff',
                      color: '#000',
                      borderRadius: 4,
                      boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
                      zIndex: 1000,
                      minWidth: 100,
                      padding: 8
                    }}
                  >
                    {languages.map(lang => (
                      <div
                        key={lang.code}
                        onClick={() => handleSelect(lang.code)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          fontWeight: i18n.language === lang.code ? 'bold' : 'normal'
                        }}
                      >
                        {lang.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </VerticalLayout>

            <VerticalLayout>
              <TitleNormalBold fontSize={pxToRem(16)} color='white'>{t('CONTACT')}</TitleNormalBold>
              <BTBox height={12} />
              <a href="mailto:bbmaster@bbtech.co.kr" style={{ cursor: 'pointer' }}>
                <TitleNormal fontSize={pxToRem(16)} color="white">bbmaster@bbtech.co.kr</TitleNormal>
              </a>
              <BTBox height={12} />
              <a href="tel:03180090777" style={{ cursor: 'pointer' }}>
                <TitleNormal fontSize={pxToRem(16)} color="white">031-8009-0777</TitleNormal>
              </a>
              <BTBox height={40} />
              <HorizontalLayout wrapContents={true} style={{ alignItems: 'center' }}>
                <TitleNormal
                  fontSize={pxToRem(16)}
                  color="white"
                  style={{ fontWeight: '700', cursor: 'pointer' }}
                  onClick={() => {
                    window.open('https://www.bbtechamerica.com/', '_blank');
                  }}
                >
                  {t('비비테크 아메리카')}
                </TitleNormal>
                <BTBox width={6} />
                <img src={ArrowRightGrey} />
              </HorizontalLayout>
              <BTBox height={40} />
              <HorizontalLayout>
                <img src={WebAwardLogo} />
                <BTBox width={17} />
                <VerticalLayout>
                  <BTBox height={13} />
                  <TitleNormal color='white' fontSize={pxToRem(14)} lineHeight='17px'>
                    <Trans i18nKey='2025 웹어워드 코리아 최우수상 수상' />
                  </TitleNormal>
                </VerticalLayout>
              </HorizontalLayout>
            </VerticalLayout>
          </HorizontalLayout>
        </VerticalLayout>
      </HorizontalLayout>
    </VerticalLayout>
  );
};

export default FooterSection;
